import { queryOptions } from '@tanstack/react-query';
import { api, apiMutation, mutation } from '../../../api/http';
import type { AdminProfile, LoginCredentials, LoginResponse } from './types';
import { clearAllDraftRecoveries } from '../../articles/api/draft-recovery';

export const authenticationQueryKeys = {
  me: ['auth', 'me'] as const,
};

export const meQuery = queryOptions({
  queryKey: authenticationQueryKeys.me,
  queryFn: ({ signal }) => api<AdminProfile>('/auth/me', { signal }),
});

export function signIn(credentials: LoginCredentials): Promise<LoginResponse> {
  return mutation<LoginResponse>('/auth/login', credentials);
}

export function completeTwoFactor(challenge: string, code: string): Promise<LoginResponse> {
  return mutation<LoginResponse>('/auth/two-factor-login', { challenge, code });
}

export interface TwoFactorStatus {
  enabled: boolean;
}

export interface TwoFactorEnrollment {
  secret: string;
  otpauthUri: string;
  qrCodeDataUri: string;
}

export interface RecoveryCodes {
  recoveryCodes: string[];
}

export const twoFactorApi = {
  start: () => mutation<TwoFactorEnrollment>('/auth/two-factor/enrollment', {}),
  confirm: (code: string) => mutation<RecoveryCodes>('/auth/two-factor/confirm', { code }),
  disable: (currentPassword: string, code: string) =>
    mutation<void>('/auth/two-factor/disable', { currentPassword, code }),
  regenerate: (code: string) =>
    mutation<RecoveryCodes>('/auth/two-factor/recovery-codes', { code }),
};

export function submitAuthFlow(
  path: string,
  values: Record<string, string | undefined>,
): Promise<void> {
  return mutation<void>(`/auth/${path}`, values);
}

export interface ManagedUser {
  id: string;
  email: string;
  displayName: string;
  status: string;
  role: string | null;
  createdAt: string;
  mustChangePassword: boolean;
  twoFactorExempt: boolean;
}

export const usersQuery = (status: string, page: number) =>
  queryOptions({
    queryKey: ['auth', 'users', status, page] as const,
    queryFn: ({ signal }) =>
      api<{ items: ManagedUser[]; total: number; page: number; pageSize: number }>(
        `/auth/users?status=${encodeURIComponent(status)}&page=${page}`,
        { signal },
      ),
  });

export function accountAction(id: string, action: string): Promise<{ temporaryPassword?: string }> {
  return mutation(`/auth/users/${id}/${action}`, {});
}

export function changeUserRole(
  id: string,
  action: 'promote' | 'demote',
  totpCode: string,
): Promise<void> {
  return mutation<void>(`/auth/users/${id}/${action}`, { totpCode });
}

export function setUserTwoFactorExemption(
  id: string,
  exempt: boolean,
  totpCode: string,
): Promise<void> {
  return apiMutation<void>(`/auth/users/${id}/two-factor-exemption`, 'PUT', { exempt, totpCode });
}

export async function signOut(): Promise<void> {
  await mutation<void>('/auth/logout');
  clearAllDraftRecoveries();
}
